import { useGetAllPersonalityQuestions } from "@/api/question/useGetAllPersonalityQuestions";
import { Button, Checkbox, Group, Modal, ScrollArea, Stack, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";

interface QuestionFilterModalProps {
  opened: boolean;
  onClose: () => void;
  selectedQuestionIds: number[];
  onApply: (ids: number[]) => void;
}

export const QuestionFilterModal = ({
  opened,
  onClose,
  selectedQuestionIds,
  onApply,
}: QuestionFilterModalProps) => {
  const { data: questionsData, isLoading } = useGetAllPersonalityQuestions();
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>(selectedQuestionIds);
  const prevOpened = useRef(opened);

  useEffect(() => {
    // Only reset local state when modal opens (not on every render)
    if (opened && !prevOpened.current) {
      setLocalSelectedIds(selectedQuestionIds);
    }
    prevOpened.current = opened;
  }, [opened, selectedQuestionIds]);

  const questions = questionsData?.questions || [];
  const allQuestionIds = questions.map((q) => q.id);

  const handleToggle = (id: number) => {
    setLocalSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((qid) => qid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setLocalSelectedIds(allQuestionIds);
  };

  const handleDeselectAll = () => {
    setLocalSelectedIds([]);
  };

  const handleApply = () => {
    onApply(localSelectedIds);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelectedIds(selectedQuestionIds);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title="질문 필터"
      size="lg"
      styles={{
        title: { fontWeight: 600, fontSize: "1.25rem" },
      }}
    >
      <Stack gap="md">
        <Group gap="xs">
          <Button size="xs" variant="outline" onClick={handleSelectAll}>
            모두 선택
          </Button>
          <Button size="xs" variant="outline" color="gray" onClick={handleDeselectAll}>
            모두 해제
          </Button>
          <Text size="sm" c="dimmed">
            ({localSelectedIds.length} / {allQuestionIds.length} 선택됨)
          </Text>
        </Group>

        <ScrollArea h={400} offsetScrollbars>
          {isLoading ? (
            <Text c="dimmed">로딩 중...</Text>
          ) : (
            <Stack gap="xs">
              {questions.map((question) => (
                <Checkbox
                  key={question.id}
                  label={question.title}
                  checked={localSelectedIds.includes(question.id)}
                  onChange={() => handleToggle(question.id)}
                  styles={{
                    label: { cursor: "pointer", color: "black" },
                  }}
                />
              ))}
            </Stack>
          )}
        </ScrollArea>

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={handleCancel}>
            취소
          </Button>
          <Button onClick={handleApply}>적용</Button>
        </Group>
      </Stack>
    </Modal>
  );
};
